import { Injectable } from '@angular/core'
import { ApiService } from '@app/core/graphql/services/api.service'
import { ServicesSubscriberEvent } from '@app/models/others.model'
import { MessageService } from 'primeng/api'
import { Subject, map, tap } from 'rxjs'
import { DELETE_RULE_NOTIFICATION, GET_RULE_BASED_NOTIFICATIONS, GET_RULE_NOTIFICATIONS_DETAILS, UPSERT_RULE_NOTIFICATIONS } from '@app/query/message.query'
import { CreateRuleNotificationInput, UpdateRuleNotificationInput } from '@app/models/message.model'
import { TranslateMessagesService } from './translate-messages.service'
import { TranslateService } from '@ngx-translate/core'

interface RuleNotificationsResponse {
  success?: boolean
  data?: UpdateRuleNotificationInput[]
}

interface RuleMutationResponse {
  success?: boolean
  data?: Record<string, unknown>
}

@Injectable({
  providedIn: 'root',
})
export class RuleBasedNotificationsService {
  messages: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  sysMessage: ReturnType<TranslateMessagesService['create']>

  id: string = 'RULE_BASED_MESSAGES'

  constructor(private API: ApiService, private messageService: MessageService, private translateMessagesService: TranslateMessagesService, private translate: TranslateService) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  getRuleBaseNotifications() {
    return this.API.privateQuery<RuleNotificationsResponse>(GET_RULE_BASED_NOTIFICATIONS, (data: Record<string, unknown>) => {
      return data
    }).pipe(
      map((response) => {
        const res = response as RuleNotificationsResponse
        if (res?.data) {
          return res.data
        } else {
          return [] as UpdateRuleNotificationInput[]
        }
      }),
      tap((messages: UpdateRuleNotificationInput[]) => {
        this.messages.next({
          type: `GET_RULE_BASED_NOTIFICATIONS`,
          data: messages,
        })
      })
    )
  }

  /**
   * Get rule-based notifications details
   * @returns
   */
  getRuleBasedMessagesDetails() {
    return this.API.privateQuery<RuleNotificationsResponse>(GET_RULE_NOTIFICATIONS_DETAILS, (data: Record<string, unknown>) => {
      return { data }
    }).pipe(
      map((response) => {
        const res = response as RuleNotificationsResponse
        if (res?.data) {
          return res.data
        } else {
          return [] as UpdateRuleNotificationInput[]
        }
      }),
      tap((messages: UpdateRuleNotificationInput[]) => {
        this.messages.next({
          type: `GET_RULE_NOTIFICATIONS_DETAILS`,
          data: messages,
        })
      })
    )
  }

  createNew(payload: Partial<CreateRuleNotificationInput[]>) {
    return this.API.privateMutation<RuleMutationResponse>(UPSERT_RULE_NOTIFICATIONS, () => {
      return {
        data: payload,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          this.messageService.add({
            summary: this.translate.instant('SYSTEM.MESSAGES.CREATE_SUCCESS.TITLE'),
            detail: this.translate.instant('MESSAGES.CREATE_MESSAGE', data.data),
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
          })
          this.messages.next({
            type: `SET_${this.id}`,
            data: data.data,
          })
        }
      })
    )
  }

  update(payload: Partial<UpdateRuleNotificationInput[]>) {
    return this.API.privateMutation<RuleMutationResponse>(UPSERT_RULE_NOTIFICATIONS, () => {
      return {
        data: payload,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          this.messageService.add({
            summary: this.translate.instant('SYSTEM.MESSAGES.UPDATE_SUCCESS.TITLE'),
            detail: this.translate.instant('MESSAGES.UPDATE_MESSAGE', data.data),
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
          })
          this.messages.next({
            type: `UPDATE_${this.id}`,
            data: data.data,
          })
        }
      })
    )
  }

  delete(ruleId: number) {
    return this.API.privateMutation<RuleMutationResponse>(DELETE_RULE_NOTIFICATION, () => {
      return {
        rule_id: ruleId,
      }
    }).pipe(
      tap((data) => {
        if (data.success) {
          this.messageService.add({
            summary: this.translate.instant('SYSTEM.MESSAGES.DELETE_SUCCESS.TITLE'),
            detail: this.translate.instant('MESSAGES.DELETE_MESSAGE', data.data),
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
          })
          this.messages.next({
            type: `DELETE_${this.id}`,
            data: data.data,
          })
        }
      })
    )
  }
}
